/**
 * 商家中心（v3.5）—— 链上推广活动（promo）全流程：
 * 入驻条件（stake ≥ merchant_min_stake）→ 创建推广活动（预算托管 PROMO_ESCROW）→
 * 活动列表 / 核销流水 → 消费者核销（五方分账：商家 85% / 推广者 8% / 返现 3% / 金库 2% / 燃烧 2%）。
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { QrCodeView } from '@/components/QrCodeView';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { formatInt, parseDpb } from '@/lib/format';
import { buildPayUri } from '@/lib/qrPay';
import { MeshLinkError } from '@/lib/result';
import { fetchMerchantProfiles, getMyLocation, saveMerchantProfile } from '@/lib/merchantApi';
import { PromoCenterBoard } from '@/features/promo/PromoCenterPage';
import type { Address, PromoCreateReq, PromoRedeemReq } from '@shared/chain';

interface PromoRow {
  promo_id: string;
  from: string;
  budget: number;
  remaining: number;
  coupon_value: number;
  max_uses: number;
  used: number;
  expires_height: number;
  promoter_bps: number;
  created_height: number;
}

interface Redemption {
  promo_id: string;
  from: string;
  amount: number;
  height: number;
  ts: number;
}

const SHARE_RULE = [
  ['商家', '85%', '核销时按活动面额分账给商家'],
  ['推广者', '8%', '推荐人佣金（无推荐人归金库）'],
  ['消费者', '3%', '核销返现'],
  ['金库', '2%', '平台金库'],
  ['燃烧', '2%', '离开流通'],
] as const;

export function MerchantPage(): JSX.Element {
  const address = useIdentityStore((s) => s.address) as Address | undefined;
  // promoList 金额字段为链上 DPB 数值（非微单位），直接格式化展示
  const dpbVal = (v: number | undefined): string =>
    Number(v ?? 0).toLocaleString('zh-CN', { maximumFractionDigits: 4 });
  const [height, setHeight] = useState(0);
  const [stats, setStats] = useState<Record<string, unknown>>({});
  const [promos, setPromos] = useState<PromoRow[]>([]);
  const [reds, setReds] = useState<Redemption[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  // 创建活动表单
  const [budget, setBudget] = useState('100');
  const [coupon, setCoupon] = useState('10');
  const [uses, setUses] = useState('100');
  const [expires, setExpires] = useState('200');
  const [bps, setBps] = useState('800');
  // 消费者核销表单
  const [redeemId, setRedeemId] = useState('');
  const [redeemAmt, setRedeemAmt] = useState('1');
  // v3.31 商家 DPB 抵扣：本店抵扣率（滑杆，链上 min/max 护栏）
  const [deductRate, setDeductRate] = useState('0.15');
  // v3.35 LBS 店铺档案
  const [shopName, setShopName] = useState('');
  const [shopCategory, setShopCategory] = useState('');
  const [shopCity, setShopCity] = useState('');
  const [shopAddr, setShopAddr] = useState('');
  const [shopDesc, setShopDesc] = useState('');
  const [shopLat, setShopLat] = useState('');
  const [shopLng, setShopLng] = useState('');
  const [shopSaved, setShopSaved] = useState(false);
  const [locating, setLocating] = useState(false);
  const [savingShop, setSavingShop] = useState(false);
  // v3.35c 商家入驻：质押保证金（链端 relay_stake，质押后解锁创建推广活动）
  const [stakeAmount, setStakeAmount] = useState('100');
  const [staking, setStaking] = useState(false);
  // v3.38 商家飞轮：拼团 / 订阅月卡 / 异业互推 / 质押理财 / 核销增强
  const [gCoupon, setGCoupon] = useState('0.5');
  const [gTargetN, setGTargetN] = useState('3');
  const [gBudget, setGBudget] = useState('5');
  const [gExpires, setGExpires] = useState('1000');
  const [gId, setGId] = useState('');
  const [sSubId, setSSubId] = useState('gold');
  const [sCoupon, setSCoupon] = useState('0.5');
  const [sCount, setSCount] = useState('10');
  const [sPrice, setSPrice] = useState('4');
  const [sValid, setSValid] = useState('5000');
  const [xPartner, setXPartner] = useState('');
  const [xCoupon, setXCoupon] = useState('0.5');
  const [xBudget, setXBudget] = useState('5');
  const [redeemRef, setRedeemRef] = useState('');
  const [redeemPoints, setRedeemPoints] = useState(false);
  const [flying, setFlying] = useState(false);
  // v3.39 商家链式发展：让利比例（3%~20%，链端 rebate_min/rebate_max 护栏）
  const [rebateRate, setRebateRate] = useState('0.10');
  const [bindMerchant, setBindMerchant] = useState('');   // 锁客绑定：对方商家地址
  const [promoCenterOpen, setPromoCenterOpen] = useState(false);

  const params = (stats?.params ?? {}) as Record<string, number>;
  const deductMin = Number(params.merchant_deduct_min ?? 0.03);
  const deductMax = Number(params.merchant_deduct_max ?? 0.3);
  const highReview = Number(params.merchant_deduct_high_review ?? 0.3);
  const subsidyRatio = Number(params.platform_subsidy_ratio ?? 0);
  const subsidyMode = Number(params.subsidy_phase_mode ?? 0);
  const singleCap = Number(params.user_deduct_single_cap ?? 200);
  const deductPct = Math.max(0, Math.min(100, Math.round((Number(deductRate) || 0) * 10000) / 100));
  const needsReview = Number(deductRate) >= highReview - 1e-9;
  /** v3.39 本商家锁客数：locked_customers[本店] 消费者数（核销自动锁客累计）。 */
  const lockedByMe = address
    ? ((stats?.locked_customers as Record<string, Record<string, unknown>> | undefined)?.[address] ?? {})
    : {};
  const myLockedCount = Object.keys(lockedByMe).length;
  const stakes = (stats?.stakes_detail ?? {}) as Record<string, { amount?: number }>;
  const myStake = address ? Number(stakes[address]?.amount ?? 0) : 0;
  const minStake = Number(params.merchant_min_stake ?? 100);
  const qualified = myStake >= minStake;

  const refresh = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      const [h, st, pl] = await Promise.all([
        container.chain.getBlockHeight().catch(() => 0),
        container.chain.meshStats().catch(() => ({}) as Record<string, unknown>),
        container.chain.promoList().catch(() => ({ items: [], redemptions: [] }) as Record<string, unknown>),
      ]);
      setHeight(h);
      setStats(st ?? {});
      const items = ((pl?.items ?? []) as PromoRow[]).filter((x) =>
        address ? x.from?.toLowerCase() === address.toLowerCase() : true,
      );
      setPromos(items);
      setReds((pl?.redemptions ?? []) as Redemption[]);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  }, [address]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createPromo = async (): Promise<void> => {
    if (!address) return;
    const b = parseDpb(budget);
    const c = parseDpb(coupon);
    if (b === '0' || c === '0') {
      setMsg('预算与券面额必须大于 0');
      return;
    }
    setBusy(true);
    try {
      const req: PromoCreateReq = {
        merchant: address,
        budgetMicro: b,
        couponValueMicro: c,
        maxUses: Math.max(1, Math.floor(Number(uses) || 1)),
        expiresHeight: Math.max(0, Math.floor(Number(expires) || 0)),
        promoterBps: Math.max(0, Math.min(4000, Math.floor(Number(bps) || 0))),
        signature: container.identity.signObject({ action: 'promo_create', from: address, ts: Date.now() }),
      };
      // v3.38 地理围栏：创建活动登记店铺位置（取自店铺档案，核销时校验距离）
      if (shopLat && shopLng) {
        req.lat = Number(shopLat);
        req.lng = Number(shopLng);
      }
      // v3.39 让利比例：3%~20%（链端 rebate_min/rebate_max 护栏，超标链端拒绝）
      const rr = Number(rebateRate);
      if (Number.isFinite(rr) && rr > 0) req.rebateRate = Math.max(0.03, Math.min(0.2, rr));
      await container.chain.promoCreate(req);
      setMsg('推广活动已创建，预算已入链上托管（PROMO_ESCROW）');
      void refresh();
    } catch (err) {
      setMsg(`创建失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const redeemPromo = async (): Promise<void> => {
    if (!address) return;
    const amt = parseDpb(redeemAmt);
    if (!redeemId.trim() || amt === '0') {
      setMsg('请输入推广 ID 与核销金额');
      return;
    }
    setBusy(true);
    try {
      const req: PromoRedeemReq = {
        consumer: address,
        promoId: redeemId.trim(),
        amountMicro: amt,
        signature: container.identity.signObject({ action: 'promo_redeem', from: address, promo_id: redeemId.trim(), ts: Date.now() }),
      };
      // v3.38 核销增强：即时分享 ref / 积分抵扣 / 地理围栏定位
      if (redeemRef.trim()) req.ref = redeemRef.trim() as Address;
      if (redeemPoints) req.usePoints = true;
      if (shopLat && shopLng) {
        req.lat = Number(shopLat);
        req.lng = Number(shopLng);
      }
      await container.chain.promoRedeem(req);
      setMsg('核销成功（五方分账已执行，商家/推广/返现已到账）');
      void refresh();
    } catch (err) {
      setMsg(`核销失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  // ─────────────── v3.38 商家飞轮动作 ───────────────

  const signTx = (extra: Record<string, unknown>): string =>
    container.identity.signObject({ ts: Date.now(), ...extra });

  const createGroupon = async (): Promise<void> => {
    if (!address) return;
    setFlying(true);
    try {
      // expiresHeight 为绝对高度：当前高度 + 有效期块数
      const h = await container.chain.getBlockHeight();
      await container.chain.groupon({
        action: 'create', from: address,
        couponValueMicro: parseDpb(gCoupon), targetN: Math.max(2, Math.floor(Number(gTargetN) || 2)),
        budgetMicro: parseDpb(gBudget), expiresHeight: h + Math.max(1, Math.floor(Number(gExpires) || 1)),
        signature: signTx({ action: 'groupon_create', from: address }),
      });
      useUiStore.getState().pushToast('success', '拼团已创建：把拼团码发给顾客，满员全员得拼团券');
    } catch (err) {
      useUiStore.getState().pushToast('error', `拼团创建失败：${err instanceof Error ? err.message : String(err)}`);
    } finally { setFlying(false); }
  };

  const joinGroupon = async (): Promise<void> => {
    if (!address) return;
    if (!gId.trim()) { useUiStore.getState().pushToast('error', '请输入拼团码'); return; }
    setFlying(true);
    try {
      await container.chain.groupon({ action: 'join', from: address, grouponId: gId.trim(), signature: signTx({ action: 'groupon_join', from: address, groupon_id: gId.trim() }) });
      useUiStore.getState().pushToast('success', '已参团：满员后全员发放拼团券，核销自动抵扣');
    } catch (err) {
      useUiStore.getState().pushToast('error', `参团失败：${err instanceof Error ? err.message : String(err)}`);
    } finally { setFlying(false); }
  };

  const createSub = async (): Promise<void> => {
    if (!address) return;
    setFlying(true);
    try {
      await container.chain.subscription({
        action: 'create', from: address, subId: sSubId.trim(),
        couponValueMicro: parseDpb(sCoupon), count: Math.max(1, Math.floor(Number(sCount) || 1)),
        priceMicro: parseDpb(sPrice), validBlocks: Math.max(1, Math.floor(Number(sValid) || 1)),
        signature: signTx({ action: 'sub_create', from: address, sub_id: sSubId.trim() }),
      });
      useUiStore.getState().pushToast('success', '月卡已上架：顾客购买后每次核销自动扣 1 次并抵扣券值');
    } catch (err) {
      useUiStore.getState().pushToast('error', `月卡创建失败：${err instanceof Error ? err.message : String(err)}`);
    } finally { setFlying(false); }
  };

  const createCross = async (): Promise<void> => {
    if (!address) return;
    if (!xPartner.trim()) { useUiStore.getState().pushToast('error', '请输入伙伴商家地址'); return; }
    setFlying(true);
    try {
      await container.chain.cross({
        from: address, partner: xPartner.trim() as Address,
        couponValueMicro: parseDpb(xCoupon), budgetMicro: parseDpb(xBudget),
        signature: signTx({ action: 'cross_create', from: address, partner: xPartner.trim() }),
      });
      useUiStore.getState().pushToast('success', '异业互推已配置：你的顾客核销后自动得伙伴店券，伙伴店核销时抵扣（你的预算出）');
    } catch (err) {
      useUiStore.getState().pushToast('error', `异业配置失败：${err instanceof Error ? err.message : String(err)}`);
    } finally { setFlying(false); }
  };

  const claimInterest = async (): Promise<void> => {
    if (!address) return;
    setFlying(true);
    try {
      await container.chain.stakeInterest({ from: address, signature: signTx({ action: 'stake_interest', from: address }) });
      useUiStore.getState().pushToast('success', '质押利息已领取（按质押额×年化×高度差，金库出）');
    } catch (err) {
      useUiStore.getState().pushToast('error', `领取失败：${err instanceof Error ? err.message : String(err)}`);
    } finally { setFlying(false); }
  };

  const mineReds = reds.filter((r) =>
    promos.some((p) => p.promo_id === r.promo_id),
  );

  /** v3.39 显式锁客绑定：消费者主动绑定常驻商家（核销自动锁客为主，此操作用于补录）。 */
  const bindLocked = async (): Promise<void> => {
    if (!address || !bindMerchant.trim()) return;
    setBusy(true);
    try {
      await container.chain.lockCustomer({
        action: 'bind',
        from: address,
        merchant: bindMerchant.trim() as Address,
        signature: signTx({ action: 'lock_customer', from: address, merchant: bindMerchant.trim() }),
      });
      useUiStore.getState().pushToast('success', '锁客绑定已上链（复购将获得加成返佣）');
      setBindMerchant('');
      void refresh();
    } catch (err) {
      useUiStore.getState().pushToast('error', `绑定失败：${err instanceof Error ? err.message : String(err)}`);
    } finally { setBusy(false); }
  };

  /** v3.34 经营概览：全部由链上 promo/核销流水现算（金额口径：链上 DPB 数值）。 */
  const totalRedeemed = mineReds.reduce((s, r) => s + Number(r.amount ?? 0), 0);
  const redeemedCount = mineReds.length;
  const totalBudget = promos.reduce((s, p) => s + Number(p.budget ?? 0), 0);
  const burnRate = totalBudget > 0 ? (totalRedeemed / totalBudget) * 100 : 0;
  const activePromos = promos.filter((p) => height <= p.expires_height && p.used < p.max_uses).length;
  const estSettled = totalRedeemed * 0.85; // 五方分账：商家 85%

  const copyPromoId = async (pid: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(pid);
      useUiStore.getState().pushToast('success', '推广码已复制，发给消费者即可核销');
    } catch {
      useUiStore.getState().pushToast('error', '复制失败，请手动复制推广码');
    }
  };

  /** v3.35 裂变优惠券：复制含推荐人的核销链接（好友打开 → 自动绑定 → 核销后我拿 8% 佣金）。 */
  const copyShareLink = async (pid: string): Promise<void> => {
    const ref = address?.toLowerCase() ?? '';
    const link = `https://mesh.doup.cn/redeem?pid=${encodeURIComponent(pid)}&ref=${ref}`;
    try {
      await navigator.clipboard.writeText(link);
      useUiStore.getState().pushToast('success', '优惠券分享链接已复制（好友核销后你获 8% 推广佣金）');
    } catch {
      useUiStore.getState().pushToast('error', '复制失败，请手动复制链接');
    }
  };

  /** v3.35 店铺档案：拉我的档案回填（用于附近商家展示）。 */
  const loadMyProfile = useCallback(async (): Promise<void> => {
    if (!address) return;
    try {
      const list = await fetchMerchantProfiles();
      const mine = list.find((p) => p.address.toLowerCase() === address.toLowerCase());
      if (mine) {
        setShopName(mine.name);
        setShopCategory(mine.category);
        setShopCity(mine.city);
        setShopAddr(mine.addr);
        setShopDesc(mine.desc);
        setShopLat(mine.lat ? String(mine.lat) : '');
        setShopLng(mine.lng ? String(mine.lng) : '');
        setShopSaved(true);
      }
    } catch {
      // 档案服务不可达不阻断商家中心主体功能
    }
  }, [address]);

  useEffect(() => {
    void loadMyProfile();
  }, [loadMyProfile]);

  /** 定位我的经纬度（浏览器 Geolocation，手机/桌面均支持）。 */
  const locateMe = async (): Promise<void> => {
    setLocating(true);
    const loc = await getMyLocation();
    if (loc) {
      setShopLat(loc.lat.toFixed(6));
      setShopLng(loc.lng.toFixed(6));
      useUiStore.getState().pushToast('success', '已获取当前位置');
    } else {
      useUiStore.getState().pushToast('error', '定位失败：请允许位置权限或手动输入经纬度');
    }
    setLocating(false);
  };

  const saveShop = async (): Promise<void> => {
    if (!address) return;
    setSavingShop(true);
    try {
      const profile = await saveMerchantProfile({
        address,
        name: shopName,
        category: shopCategory,
        city: shopCity,
        addr: shopAddr,
        lat: shopLat ? Number(shopLat) : undefined,
        lng: shopLng ? Number(shopLng) : undefined,
        desc: shopDesc,
      });
      setShopName(profile.name);
      setShopLat(profile.lat ? String(profile.lat) : '');
      setShopLng(profile.lng ? String(profile.lng) : '');
      setShopSaved(true);
      useUiStore.getState().pushToast('success', '店铺信息已保存，将出现在「附近商家」');
    } catch (err) {
      useUiStore.getState().pushToast('error', err instanceof Error ? err.message : '保存失败');
    } finally {
      setSavingShop(false);
    }
  };

  /** v3.35c 质押保证金：质押后满足商家门槛即可创建推广活动（服务端签名上链）。 */
  const doStake = async (): Promise<void> => {
    if (!address) return;
    const amt = parseDpb(stakeAmount);
    if (amt === '0') {
      useUiStore.getState().pushToast('error', '质押金额必须大于 0');
      return;
    }
    const peerId = useIdentityStore.getState().peerId || address.toLowerCase().slice(0, 40);
    // v3.37 商家邀请返佣：从邀请链接 ?ref= 读取推荐商家地址（首次质押时绑定）
    const ref = new URLSearchParams(window.location.search).get('ref') ?? undefined;
    setStaking(true);
    try {
      await container.chain.relayStake({
        action: 'stake',
        address,
        amountMicro: amt,
        peerId,
        ...(ref ? { ref: ref as Address } : {}),
      });
      if (ref) {
        useUiStore.getState().pushToast('success',
          `质押成功（${dpbVal(Number(stakeAmount))} DPB），已绑定邀请商家 ${ref.slice(0, 10)}…，其核销商家份额的 5% 将返佣给你`);
      } else {
        useUiStore.getState().pushToast('success', `质押成功（${dpbVal(Number(stakeAmount))} DPB 已入链上托管），已具备商家资格`);
      }
      void refresh();
    } catch (err) {
      useUiStore.getState().pushToast('error', `质押失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setStaking(false);
    }
  };

  /** v3.35c 解押：链端锁定 unstake_lock 块后自动返还。 */
  const doUnstake = async (): Promise<void> => {
    if (!address) return;
    setStaking(true);
    try {
      await container.chain.relayStake({ action: 'unstake', address, amountMicro: '0' });
      useUiStore.getState().pushToast('success', `解押申请已提交：${Number(params.unstake_lock ?? 0)} 块后自动返还质押`);
      void refresh();
    } catch (err) {
      useUiStore.getState().pushToast('error', `解押失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setStaking(false);
    }
  };

  /** v3.35 核销趋势：近 20 块柱状（SVG，无外部依赖）。 */
  const trend = useMemo(() => {
    if (mineReds.length === 0 || height <= 0) return null;
    const buckets = new Map<number, number>();
    for (const r of mineReds) {
      const h = Math.floor(Number(r.height ?? 0));
      buckets.set(h, (buckets.get(h) ?? 0) + 1);
    }
    const start = Math.max(0, height - 20);
    const arr: { h: number; n: number }[] = [];
    for (let h = start; h <= height; h += 1) arr.push({ h, n: buckets.get(h) ?? 0 });
    const max = Math.max(1, ...arr.map((x) => x.n));
    const w = 520;
    const hgt = 96;
    const barW = Math.max(2, Math.min(10, Math.floor(w / arr.length) - 2));
    const bars = arr
      .map((x, i) => {
        const bh = x.n > 0 ? Math.max(3, (x.n / max) * (hgt - 20)) : 0;
        const xPos = 12 + i * ((w - 24) / arr.length);
        return (
          <rect key={x.h} x={xPos} y={hgt - 18 - bh} width={barW} height={bh} rx={1.5}
            fill={x.n > 0 ? '#22c55e' : '#3b5566'} opacity={x.n > 0 ? 0.95 : 0.35}>
            <title>{`高度 ${x.h}：${x.n} 笔核销`}</title>
          </rect>
        );
      });
    return { arr, max, w, hgt, bars };
  }, [mineReds, height]);

  const shareInputCls =
    'w-full rounded-btn bg-panel-2 border border-line px-3 py-2 text-sm text-text placeholder-dim outline-none focus:border-accent';
  const shareLabelCls = 'text-xs text-dim';

  const inputCls =
    'w-full rounded-btn bg-panel-2 border border-line px-3 py-2 text-sm text-text placeholder-dim outline-none focus:border-accent';
  const labelCls = 'text-xs text-dim';
  const deductPayUri = address ? buildPayUri(address, undefined, deductRate) : '';

  return (
    <div className="space-y-4">
      {/* 入驻与规则 */}
      <section className="card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-text">商家中心</h2>
          <Badge tone="ok">链高度 {formatInt(height)}</Badge>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-dim">
          链上推广（promo）机制：商家质押保证金后创建推广活动，预算进入链上托管；
          消费者核销时按五方分账，全程上链、可审计。
        </p>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{myStake.toFixed(2)}</div>
            <div className="text-[11px] text-dim">我的质押 DPB</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{minStake.toFixed(0)}</div>
            <div className="text-[11px] text-dim">商家门槛 DPB</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{promos.length}</div>
            <div className="text-[11px] text-dim">我的活动</div>
          </div>
        </div>
        <div className="mt-3">
          {address ? (
            qualified ? (
              <Badge tone="ok">已具备商家资格（质押满足门槛）</Badge>
            ) : (
              <Badge tone="warn">未达商家门槛：质押 {minStake.toFixed(0)} DPB 后即可创建推广活动</Badge>
            )
          ) : (
            <Badge tone="warn">未连接身份</Badge>
          )}
        </div>

        {/* v3.35c 质押保证金：商家入驻入口（原「交易/质押入口」无 UI，此处补全） */}
        {address ? (
          <div className="mt-3 rounded-btn bg-panel-2/60 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium text-accent">质押保证金（商家入驻）</span>
              <span className="text-[10px] text-dim">
                已质押 {dpbVal(myStake)} / 门槛 {minStake.toFixed(0)} DPB{myStake > 0 ? '（解押需锁 ' + Number(params.unstake_lock ?? 0) + ' 块）' : ''}
              </span>
            </div>
            {myStake < minStake ? (
              <div className="flex gap-2">
                <input
                  className="w-full rounded-btn bg-panel-2 border border-line px-3 py-2 text-sm text-text placeholder-dim outline-none focus:border-accent"
                  type="number"
                  min={minStake}
                  step="any"
                  value={stakeAmount}
                  onChange={(e) => setStakeAmount(e.target.value)}
                  placeholder={`质押 ≥ ${minStake.toFixed(0)} DPB`}
                />
                <Button className="shrink-0" loading={staking} onClick={() => void doStake()}>
                  质押并入驻
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-dim">质押已达标，可自由创建推广活动；如需撤回请解押。</span>
                <Button variant="outline" size="sm" loading={staking} onClick={() => void doUnstake()}>
                  申请解押
                </Button>
              </div>
            )}
          </div>
        ) : null}

        {/* v3.34 经营概览：核销额 / 笔数 / 预算消耗 / 到账预估 */}
        <div className="mt-4 rounded-btn bg-panel-2/60 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-accent">经营概览（链上现算）</span>
            <span className="text-[10px] text-dim">数据源：promo 活动 + 核销流水</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
            <div className="rounded-btn bg-panel-2 px-3 py-2">
              <div className="text-lg font-semibold text-text">{dpbVal(totalRedeemed)}</div>
              <div className="text-[11px] text-dim">累计核销 DPB</div>
            </div>
            <div className="rounded-btn bg-panel-2 px-3 py-2">
              <div className="text-lg font-semibold text-text">{redeemedCount}</div>
              <div className="text-[11px] text-dim">核销笔数</div>
            </div>
            <div className="rounded-btn bg-panel-2 px-3 py-2">
              <div className="text-lg font-semibold text-text">{burnRate.toFixed(1)}%</div>
              <div className="text-[11px] text-dim">预算消耗率</div>
            </div>
            <div className="rounded-btn bg-panel-2 px-3 py-2">
              <div className="text-lg font-semibold text-text">{dpbVal(estSettled)}</div>
              <div className="text-[11px] text-dim">预计到账(×85%)</div>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-dim">
            <span>活动 {promos.length} 个（进行中 {activePromos}）</span>
            <span>总预算 {dpbVal(totalBudget)} DPB</span>
            <span>消耗率 = 累计核销 ÷ 总预算</span>
          </div>
          {trend ? (
            <div className="mt-2">
              <div className="mb-1 flex items-center justify-between text-[11px] text-dim">
                <span>核销趋势（近 20 块 · 笔数）</span>
                <span>峰值 {trend.max} 笔</span>
              </div>
              <svg viewBox={`0 0 ${trend.w} ${trend.hgt}`} className="w-full" role="img" aria-label="核销趋势柱状图">
                <line x1={12} y1={trend.hgt - 18} x2={trend.w - 12} y2={trend.hgt - 18}
                  stroke="#3b5566" strokeWidth={1} />
                {trend.bars}
                <text x={12} y={trend.hgt - 4} fill="#8b93a1" fontSize={9}>
                  {`高度 ${height - 20} → ${height}`}
                </text>
              </svg>
            </div>
          ) : null}
        </div>

        <div className="mt-4">
          <div className="mb-1 text-xs font-medium text-accent">核销五方分账（链上固定）</div>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-5">
            {SHARE_RULE.map(([k, v, d]) => (
              <div key={k} className="rounded-btn bg-panel-2 px-3 py-2">
                <div className="text-xs font-medium text-text">{k} {v}</div>
                <div className="mt-0.5 text-[10px] leading-tight text-dim">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* v3.31 DPB 抵扣与平台补贴：商家收银台 */}
      <section className="card p-5">
        <h2 className="text-sm font-medium text-text">DPB 抵扣与平台补贴</h2>
        <p className="mt-1 text-xs leading-relaxed text-dim">
          顾客扫码付款时，可按本店抵扣率用 DPB 抵扣消费金额（商业闭环：DPB 进入真实消费）；
          平台按补贴率向商家补贴抵扣额的等值权益，前期补贴高、成熟后自动退出。
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{(deductMin * 100).toFixed(0)}%–{(deductMax * 100).toFixed(0)}%</div>
            <div className="text-[11px] text-dim">抵扣率区间（链上护栏）</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{(subsidyRatio * 100).toFixed(0)}%</div>
            <div className="text-[11px] text-dim">平台补贴率{Number(subsidyRatio) <= 0 ? '（已关闭）' : ''}</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{subsidyMode >= 1 ? '自动' : '手动'}</div>
            <div className="text-[11px] text-dim">补贴模式{subsidyMode >= 1 ? '（随商家数衰减）' : '（平台调控）'}</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{formatInt(singleCap)}</div>
            <div className="text-[11px] text-dim">单笔抵扣上限 DPB</div>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start">
          {/* 本店抵扣率设置 */}
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <span className={labelCls}>本店 DPB 抵扣率</span>
              <span className="text-sm font-semibold text-accent">{deductPct.toFixed(1)}%</span>
            </div>
            <input
              type="range"
              min={deductMin}
              max={deductMax}
              step={0.01}
              value={deductRate}
              onChange={(e) => setDeductRate(e.target.value)}
              className="w-full accent-accent"
            />
            <div className="flex justify-between text-[10px] text-dim">
              <span>{(deductMin * 100).toFixed(0)}%</span>
              <span>建议 {(Math.min(deductMax, 0.2) * 100).toFixed(0)}%</span>
              <span>{(deductMax * 100).toFixed(0)}%</span>
            </div>
            <p className="text-[11px] leading-relaxed text-dim">
              {needsReview
                ? `⚠ 抵扣率 ≥ ${(highReview * 100).toFixed(0)}% 为高抵扣档，需平台审核后生效，防止大额套利。`
                : '抵扣率越高，顾客用 DPB 抵扣越多，获客越强；平台补贴按抵扣额比例发放给商家。'}
            </p>
            <p className="text-[11px] text-dim">
              复购衰减：老顾客在同店复购时抵扣额按 ×{Number(params.user_deduct_decay ?? 0.9).toFixed(2)} 系数递减（刺激新客、控老客套利）。
            </p>
          </div>

          {/* 商家收银台码 */}
          <div className="flex shrink-0 flex-col items-center gap-2 rounded-card border border-line bg-panel-2/50 p-4">
            {address ? (
              <>
                <QrCodeView value={deductPayUri} size={180} copyable label={`商家收银台码（DPB 抵扣 ${deductPct.toFixed(1)}%）`} />
                <p className="max-w-[200px] text-center text-[11px] leading-relaxed text-dim">
                  顾客扫此码付款：输入消费金额，自动按 {deductPct.toFixed(1)}% 抵扣 DPB，其余法币线下/聚合支付结算。
                </p>
              </>
            ) : (
              <p className="text-xs text-dim">连接身份后生成商家收银台码</p>
            )}
          </div>
        </div>
      </section>

      {/* v3.35 店铺信息（LBS 附近商家）：档案存服务端，展示名/品类/位置 */}
      <section className="card p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">店铺信息（附近商家展示）</h2>
          {shopSaved ? <Badge tone="ok">已保存</Badge> : <Badge tone="warn">未设置</Badge>}
        </div>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          设置店铺名称、品类与位置后，你的推广活动将按距离出现在消费者「发现 → 商家优惠」的
          「附近商家」列表（定位开启时优先按距离排序）。
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <label className="space-y-1">
            <span className={shareLabelCls}>店铺名称</span>
            <input className={shareInputCls} value={shopName} onChange={(e) => setShopName(e.target.value)} placeholder="如：老王糖水铺" maxLength={40} />
          </label>
          <label className="space-y-1">
            <span className={shareLabelCls}>品类</span>
            <input className={shareInputCls} value={shopCategory} onChange={(e) => setShopCategory(e.target.value)} placeholder="如：餐饮 / 零售" maxLength={20} />
          </label>
          <label className="space-y-1">
            <span className={shareLabelCls}>城市</span>
            <input className={shareInputCls} value={shopCity} onChange={(e) => setShopCity(e.target.value)} placeholder="如：深圳" maxLength={20} />
          </label>
          <label className="space-y-1 sm:col-span-2">
            <span className={shareLabelCls}>详细地址</span>
            <input className={shareInputCls} value={shopAddr} onChange={(e) => setShopAddr(e.target.value)} placeholder="如：南山区科技园 1 号店" maxLength={80} />
          </label>
          <div className="flex items-end gap-1.5">
            <Button className="flex-1" variant="outline" size="sm" loading={locating} onClick={() => void locateMe()}>
              获取我的位置
            </Button>
          </div>
          <label className="space-y-1">
            <span className={shareLabelCls}>纬度 (WGS84)</span>
            <input className={shareInputCls} type="number" step="any" value={shopLat} onChange={(e) => setShopLat(e.target.value)} placeholder="如 22.540503" />
          </label>
          <label className="space-y-1">
            <span className={shareLabelCls}>经度 (WGS84)</span>
            <input className={shareInputCls} type="number" step="any" value={shopLng} onChange={(e) => setShopLng(e.target.value)} placeholder="如 113.934528" />
          </label>
          <label className="space-y-1 sm:col-span-2">
            <span className={shareLabelCls}>店铺简介</span>
            <input className={shareInputCls} value={shopDesc} onChange={(e) => setShopDesc(e.target.value)} placeholder="一句话介绍你的店（最多 200 字）" maxLength={200} />
          </label>
          <div className="flex items-end">
            <Button className="w-full" size="sm" loading={savingShop} disabled={!address} onClick={() => void saveShop()}>
              保存店铺信息
            </Button>
          </div>
        </div>

        {/* v3.37 店铺名片二维码海报：扫一扫直达店铺（地址+店名），方便线下物料投放 */}
        <div className="mt-4 flex flex-col items-center gap-2 rounded-card border border-line bg-panel-2/50 p-4 sm:flex-row sm:justify-between">
          {address ? (
            <>
              <div className="flex flex-col items-center gap-1.5">
                <QrCodeView
                  value={`https://mesh.doup.cn/merchant?addr=${encodeURIComponent(address)}${shopName ? `&name=${encodeURIComponent(shopName)}` : ''}`}
                  size={168}
                  copyable
                  label={`店铺名片（${shopName || '未命名店铺'}）`}
                />
                <p className="max-w-[220px] text-center text-[11px] leading-relaxed text-dim">
                  打印/分享此名片码：好友扫码直达你的店铺，可查看活动、核销与返佣。
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void (async () => {
                  const link = `https://mesh.doup.cn/merchant?addr=${encodeURIComponent(address)}${shopName ? `&name=${encodeURIComponent(shopName)}` : ''}`;
                  try {
                    await navigator.clipboard.writeText(link);
                    useUiStore.getState().pushToast('success', '店铺名片链接已复制，可发到群聊/朋友圈');
                  } catch {
                    useUiStore.getState().pushToast('error', '复制失败，请手动复制');
                  }
                })()}
              >
                复制名片链接
              </Button>
            </>
          ) : (
            <p className="text-xs text-dim">连接身份后生成店铺名片码</p>
          )}
        </div>
      </section>

      {/* v3.37 P0-1 商家邀请返佣：邀请好友商家入驻，其核销商家份额 5% 返佣给你（链上自动拆分） */}
      <section className="card p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">商家邀请返佣（推荐好友商家入驻）</h2>
          <Badge tone="ok">5% 返佣</Badge>
        </div>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          把你的邀请链接发给同行/好友商家：对方通过链接质押入驻后，其推广活动的每次核销，
          商家份额的 <b className="text-text">5%</b> 将链上自动拆给你（1440 块冷静期后生效，全程可审计）。
          邀请越多、被邀商家生意越好，你的被动返佣越多。
        </p>
        <div className="rounded-btn bg-panel-2/60 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-accent">我的商家邀请码（=我的地址）</span>
            {address ? <Badge tone="ok">已连接</Badge> : <Badge tone="warn">未连接身份</Badge>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-btn bg-panel-1 px-3 py-2 font-mono text-[11px] text-text">
              {address ?? '—'}
            </code>
            <Button
              variant="outline"
              size="sm"
              disabled={!address}
              onClick={() => void (async () => {
                const link = `https://mesh.doup.cn/merchant?ref=${encodeURIComponent(address?.toLowerCase() ?? '')}`;
                try {
                  await navigator.clipboard.writeText(link);
                  useUiStore.getState().pushToast('success', '商家邀请链接已复制：好友质押入驻时自动绑定你为推荐人');
                } catch {
                  useUiStore.getState().pushToast('error', '复制失败，请手动复制链接');
                }
              })()}
            >
              复制邀请链接
            </Button>
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-dim">
            被邀商家打开链接 → 质押入驻 → 链上绑定 merchant_referrals →
            其后每次核销按 5% 拆分返佣给你（冷静期内归金库防刷）。
          </p>
        </div>
      </section>

      {/* v3.37 P0-2 回头券：消费即返，下次核销自动抵扣（金库补贴，人人可得） */}
      <section className="card p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">回头券（消费返利 · 自动抵扣）</h2>
          <Badge tone="ok">已上线</Badge>
        </div>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          全链行为奖励之外的新增长钩子：任何消费者在任意商家核销成功一次，链上自动发放一张
          <b className="text-text">回头券（0.5 DPB，由平台金库补贴）</b>；下次核销时按券值自动抵扣，
          先到先抵扣，每人同时持 1 张、核销即换新，有效期 1440 块（约 4 小时）。
        </p>
        <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">0.5</div>
            <div className="text-[11px] text-dim">回头券面额 DPB</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">1 张</div>
            <div className="text-[11px] text-dim">每人持有上限</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">1440 块</div>
            <div className="text-[11px] text-dim">有效期（约 4 小时）</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">金库</div>
            <div className="text-[11px] text-dim">补贴来源（平台补贴）</div>
          </div>
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-dim">
          商家侧：回头券抵扣部分由金库出资补回活动托管，不削减你的分账；消费者侧：消费即返、复购自动省，
          形成「消费 → 返券 → 再消费」闭环。治理参数可在超管理台调整（revisit_enabled / revisit_value / revisit_lock_blocks）。
        </p>
      </section>

      {/* v3.38 商家飞轮：新客补贴 / 会员积分 / 连续消费 / 拼团 / 月卡 / 异业互推 / 质押理财 / 地理围栏 */}
      <section className="card p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">商家飞轮（v3.38 增长引擎）</h2>
          <Badge tone="ok">12 项已上线</Badge>
        </div>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          以「消费」为单一锚点，叠加新客补贴、会员积分、连续消费、拼团、月卡、异业互推、质押理财等机制，
          全部链上执行、可审计、参数可治理。目标：新客转化、老客复购、商家联动、资金沉淀。
        </p>

        {/* 新客补贴 + 分享返佣 + 积分/streak 规则 */}
        <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{(Number(params.new_customer_subsidy_ratio ?? 0.5) * 100).toFixed(0)}%</div>
            <div className="text-[11px] text-dim">新客首单补贴（券面额×此比例，金库出）</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{(Number(params.points_redeem_ratio ?? 0.01) * 100).toFixed(1)}%</div>
            <div className="text-[11px] text-dim">积分抵扣（消费 1:1 累积）</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{Number(params.streak_days ?? 7)} 天</div>
            <div className="text-[11px] text-dim">连续消费达标天数（达标日补贴）</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{Number(params.geo_fence_km ?? 2).toFixed(1)} km</div>
            <div className="text-[11px] text-dim">核销地理围栏（登记位置后生效）</div>
          </div>
        </div>
        <ul className="mt-3 space-y-1 text-[11px] leading-relaxed text-dim">
          <li>· 新客首单：消费者在某店首次核销，金库按券面额×{((Number(params.new_customer_subsidy_ratio ?? 0.5)) * 100).toFixed(0)}% 直补消费者 → 降低首单决策成本。</li>
          <li>· 会员积分：每核销 1 DPB 累积 {Number(params.points_per_dpb ?? 1).toFixed(1)} 积分，下次核销勾选「用积分抵扣」即减现（金库补贴）。</li>
          <li>· 连续消费：同一消费者连续 {Number(params.streak_days ?? 7)} 天到店核销，达标日金库额外补贴券面额×{(Number(params.streak_bonus_ratio ?? 0.5) * 100).toFixed(0)}% → 养成每日到店习惯。</li>
          <li>· 地理围栏：创建活动会登记你的店铺位置（来自店铺档案）；开启围栏后，顾客须在附近核销，防止异地刷单。</li>
        </ul>
      </section>

      {/* v3.39 商家链式发展：让利 / 锁客 / 信誉分 / 回购销毁 / 推广体系 */}
      <section className="card p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">商家链式发展（v3.39）</h2>
          <Badge tone="ok">9 机制已上线</Badge>
        </div>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          借鉴《三方共赢消费奖励平台》机制：让利返佣（3%~20%）、锁客永久绑定、商圈分红、任务中心、回购销毁、
          信誉分加成、推广者分级 V1-V5 —— 全部链上执行、参数可治理、无资金池。
        </p>

        {/* 让利比例滑块 */}
        <div className="mb-3 rounded-btn bg-panel-2 p-3">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-medium text-text">创建活动让利比例（返佣池上限）</span>
            <span className="text-sm font-semibold text-primary">{(Number(rebateRate) * 100).toFixed(0)}%</span>
          </div>
          <input
            className="w-full accent-primary"
            type="range"
            min="3" max="20" step="1"
            value={Math.round((Number(rebateRate) || 0.1) * 100)}
            onChange={(e) => setRebateRate((Number(e.target.value) / 100).toFixed(2))}
          />
          <div className="mt-1 flex justify-between text-[10px] text-dim">
            <span>3% 保底</span><span>锁客/复购返佣最高 20%</span>
          </div>
          <p className="mt-1 text-[10px] leading-relaxed text-dim">
            该比例决定每次核销从平台份额中返给推广体系的力度：直推 80% + 二级 20% + 团队级差 + 锁客复购加成（链上参数可治理）。
          </p>
        </div>

        {/* 锁客 / 信誉分 / 回购 / 商圈 四卡 */}
        <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{myLockedCount}</div>
            <div className="text-[11px] text-dim">锁客人数（复购累计）</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{address ? Number((stats?.merchant_scores as Record<string, { score?: number }>)?.[address]?.score ?? 0).toFixed(1) : '0'}</div>
            <div className="text-[11px] text-dim">商家信誉分（核销加成）</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{dpbVal(Number((stats?.buybacks as { total?: number } | undefined)?.total ?? 0))}</div>
            <div className="text-[11px] text-dim">回购销毁总量 (DPB)</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{address ? Object.keys(((stats?.district_cards as Record<string, { districts?: Record<string, number> }>)?.[address]?.districts) ?? {}).length : 0}</div>
            <div className="text-[11px] text-dim">我持有的商圈卡</div>
          </div>
        </div>
        <ul className="mt-3 space-y-1 text-[11px] leading-relaxed text-dim">
          <li>· 让利返佣：核销时平台份额内拆——直推 {Number(params.direct_bps ?? 8000) / 100}%、二级 {Number(params.second_bps ?? 2000) / 100}%、团队级差 {Number(params.rank_diff_bps ?? 500) / 100}%/级，剩余入金库。</li>
          <li>· 锁客绑定：顾客首次核销即永久锁定（spend_total 累计），复购平台份额再返商家 10%（lock_bonus），也可显式绑定。</li>
          <li>· 商圈分红：核销按经纬度归入商圈池（金库注入 5%），核销 ≥{Number(params.district_card_threshold ?? 3)} 次自动发商圈卡，持卡按贡献分池领取。</li>
          <li>· 任务中心：每日完成浏览/搜索/签到 ≥{Number(params.task_actions ?? 3)} 类行为，自动发任务奖励 {dpbVal(Number(params.task_bonus_dpb ?? 0.01))} DPB（每日一次）。</li>
          <li>· 回购销毁：平台净额 30% 即时销毁（通缩），商家信誉分 ≥{Number(params.score_min ?? 60)} 再拆 2% 给商家。</li>
        </ul>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="outline" loading={busy} disabled={!address} onClick={() => setPromoCenterOpen((v) => !v)}>
            {promoCenterOpen ? '收起推广面板' : '打开推广面板'}
          </Button>
          <label className="flex flex-1 items-center gap-2 rounded-btn bg-panel-2 px-3 py-2 text-[11px] text-dim sm:flex-none">
            显式锁客绑定商家：
            <input
              className={inputCls + ' flex-1 sm:w-64'}
              placeholder="0x…商家地址"
              value={bindMerchant}
              onChange={(e) => setBindMerchant(e.target.value)}
            />
            <Button variant="outline" loading={busy} disabled={!address || !bindMerchant.trim()} onClick={() => void bindLocked()}>
              绑定
            </Button>
          </label>
        </div>
        {promoCenterOpen && <PromoCenterBoard />}
      </section>

      {/* v3.38 拼团 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">拼团（成团发券 · 全员得利）</h2>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          创建拼团活动：顾客开团后邀请好友参团，满 {Math.max(2, Math.floor(Number(gTargetN) || 2))} 人成团，
          团内所有人获得一张<b className="text-text">拼团券（{gCoupon} DPB）</b>，核销自动抵扣（商家托管预算承担）。
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <label className="space-y-1">
            <span className={labelCls}>拼团券面额 (DPB)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={gCoupon} onChange={(e) => setGCoupon(e.target.value)} />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>成团人数 (≥2)</span>
            <input className={inputCls} type="number" min="2" value={gTargetN} onChange={(e) => setGTargetN(e.target.value)} />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>托管预算 (DPB)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={gBudget} onChange={(e) => setGBudget(e.target.value)} />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>有效期 (+N 块)</span>
            <input className={inputCls} type="number" min="1" value={gExpires} onChange={(e) => setGExpires(e.target.value)} />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <Button className="flex-1 sm:flex-none" loading={flying} disabled={!address || !qualified} onClick={() => void createGroupon()}>
            创建拼团
          </Button>
          <input className={inputCls + ' flex-1 sm:flex-none sm:w-56'} value={gId} onChange={(e) => setGId(e.target.value)} placeholder="输入他人拼团码参团" />
          <Button variant="outline" loading={flying} disabled={!address} onClick={() => void joinGroupon()}>
            参团
          </Button>
        </div>
      </section>

      {/* v3.38 订阅月卡 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">订阅月卡（预付锁客）</h2>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          顾客预付购买你的月卡（价格 ×{sCount} 次），之后每次核销自动扣 1 次并抵扣券面额（{sCoupon} DPB），
          有效期 {sValid} 块。预收款即时到账 → 锁客 + 现金流。
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <label className="space-y-1">
            <span className={labelCls}>月卡 ID</span>
            <input className={inputCls} value={sSubId} onChange={(e) => setSSubId(e.target.value)} maxLength={24} />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>每次抵扣 (DPB)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={sCoupon} onChange={(e) => setSCoupon(e.target.value)} />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>次数</span>
            <input className={inputCls} type="number" min="1" value={sCount} onChange={(e) => setSCount(e.target.value)} />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>售价 (DPB)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={sPrice} onChange={(e) => setSPrice(e.target.value)} />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>有效期 (+N 块)</span>
            <input className={inputCls} type="number" min="1" value={sValid} onChange={(e) => setSValid(e.target.value)} />
          </label>
        </div>
        <Button className="mt-3" loading={flying} disabled={!address || !qualified} onClick={() => void createSub()}>
          上架月卡
        </Button>
        <p className="mt-2 text-[11px] text-dim">消费者入口：顾客在「发现 → 商家优惠」打开你的店铺名片即可购买月卡。</p>
      </section>

      {/* v3.38 异业互推 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">异业互推（互相导流）</h2>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          指定一家伙伴商家：你的顾客在本店核销后，自动获得伙伴店的异业券（你的预算补贴）；
          伙伴店核销时抵扣你的预算。客源互通、不抢客，适合互补品类（餐饮↔娱乐、零售↔服务）。
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="space-y-1 sm:col-span-1">
            <span className={labelCls}>伙伴商家地址 (0x…)</span>
            <input className={inputCls} value={xPartner} onChange={(e) => setXPartner(e.target.value)} placeholder="0x… 伙伴质押商家" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>异业券面额 (DPB)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={xCoupon} onChange={(e) => setXCoupon(e.target.value)} />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>补贴预算 (DPB)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={xBudget} onChange={(e) => setXBudget(e.target.value)} />
          </label>
        </div>
        <Button className="mt-3" loading={flying} disabled={!address || !qualified} onClick={() => void createCross()}>
          配置异业互推
        </Button>
      </section>

      {/* v3.38 质押理财 */}
      <section className="card p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">质押理财（保证金生息）</h2>
          <Badge tone="ok">年化 {((Number(params.stake_interest_apr_bps ?? 500) / 100)).toFixed(1)}%</Badge>
        </div>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          质押在链上的保证金 DPB 按高度累计利息（年化 {(Number(params.stake_interest_apr_bps ?? 500) / 100).toFixed(1)}%，
          由平台金库支付），随时领取、累计制不重复计。商家质押不仅解锁推广资格，还持续生息。
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button loading={flying} disabled={!address || myStake <= 0} onClick={() => void claimInterest()}>
            领取质押利息
          </Button>
          <span className="text-[11px] text-dim">当前质押 {dpbVal(myStake)} DPB</span>
        </div>
      </section>

      {/* 创建推广活动 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">创建推广活动</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <label className="space-y-1">
            <span className={labelCls}>预算 (DPB)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="如 100" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>券面额 (DPB/次)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="如 10" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>核销次数上限</span>
            <input className={inputCls} type="number" min="1" value={uses} onChange={(e) => setUses(e.target.value)} placeholder="如 100" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>有效期（相对当前高度 +N 块）</span>
            <input className={inputCls} type="number" min="1" value={expires} onChange={(e) => setExpires(e.target.value)} placeholder="如 200" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>推广分成 (bps，≤4000=40%)</span>
            <input className={inputCls} type="number" min="0" max="4000" value={bps} onChange={(e) => setBps(e.target.value)} placeholder="如 800" />
          </label>
          <div className="flex items-end">
            <Button className="w-full" loading={busy} disabled={!address || !qualified} onClick={() => void createPromo()}>
              创建并托管
            </Button>
          </div>
        </div>
        {msg ? <p className="mt-3 text-xs text-accent">{msg}</p> : null}
      </section>

      {/* 我的推广活动 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">我的推广活动（{promos.length}）</h2>
        {promos.length === 0 ? (
          <EmptyState title="还没有推广活动" description="创建活动后，预算进入链上托管并生成推广 ID（卡券码）" />
        ) : (
          <div className="space-y-2">
            {promos.map((p) => {
              const alive = height <= p.expires_height && p.used < p.max_uses;
              return (
                <div key={p.promo_id} className="rounded-btn bg-panel-2 px-3 py-2.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-mono text-xs text-text">#{p.promo_id.slice(0, 16)}</div>
                    {alive ? <Badge tone="ok">进行中</Badge> : <Badge tone="warn">已结束</Badge>}
                  </div>
                  <div className="mt-1 grid grid-cols-3 gap-1 text-[11px] text-dim sm:grid-cols-6">
                    <span>预算 {dpbVal(p.budget)}</span>
                    <span>券值 {dpbVal(p.coupon_value)}</span>
                    <span>核销 {p.used}/{p.max_uses}</span>
                    <span>剩余 {dpbVal(p.remaining)}</span>
                    <span>有效期至 {formatInt(p.expires_height)}</span>
                    <span>推广 {((p.promoter_bps ?? 0) / 100).toFixed(1)}%</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono text-[10px] text-dim">推广码（消费者核销用）：{p.promo_id}</span>
                    <div className="flex items-center gap-1.5">
                      {alive ? (
                        <>
                          <Button variant="outline" size="sm" onClick={() => void copyPromoId(p.promo_id)}>
                            复制核销码
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => void copyShareLink(p.promo_id)}>
                            分享券（带推荐）
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* 消费者核销 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">消费核销</h2>
        <p className="mb-3 text-xs text-dim">输入推广码（promo_id）与消费金额，核销后五方分账自动执行，消费者获得返现。</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="space-y-1">
            <span className={labelCls}>推广码 promo_id</span>
            <input className={inputCls} value={redeemId} onChange={(e) => setRedeemId(e.target.value)} placeholder="16 位 hex 推广码" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>核销金额 (DPB)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={redeemAmt} onChange={(e) => setRedeemAmt(e.target.value)} placeholder="如 1" />
          </label>
          <div className="flex items-end">
            <Button className="w-full" variant="outline" loading={busy} disabled={!address} onClick={() => void redeemPromo()}>
              核销
            </Button>
          </div>
        </div>
        {/* v3.38 核销增强：即时分享返佣 / 积分抵扣 / 地理围栏（定位取自店铺档案） */}
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="space-y-1">
            <span className={labelCls}>分享者地址（本次核销的推广人，0x…，选填）</span>
            <input className={inputCls} value={redeemRef} onChange={(e) => setRedeemRef(e.target.value)} placeholder="填谁，本次 8% 推广佣金归谁（即时生效）" />
          </label>
          <label className="flex items-center gap-2 rounded-btn bg-panel-2 px-3 py-2">
            <input type="checkbox" checked={redeemPoints} onChange={(e) => setRedeemPoints(e.target.checked)} className="accent-accent" />
            <span className="text-xs text-text">本次使用会员积分抵扣（消费 1:1 累积，100 积分≈1 DPB）</span>
          </label>
        </div>
        {shopLat && shopLng ? (
          <p className="mt-2 text-[11px] text-dim">地理围栏：核销定位将取店铺档案坐标（{Number(shopLat).toFixed(4)}, {Number(shopLng).toFixed(4)}），未登记定位则围栏不生效。</p>
        ) : null}
      </section>

      {/* 核销流水 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">相关核销流水（{mineReds.length}）</h2>
        {mineReds.length === 0 ? (
          <EmptyState title="暂无核销记录" description="消费者核销后，分账流水将在此展示" />
        ) : (
          <div className="space-y-2">
            {mineReds.slice().reverse().map((r, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-btn bg-panel-2 px-3 py-2 text-xs">
                <span className="font-mono text-text">#{r.promo_id.slice(0, 16)}</span>
                <span className="text-dim">{dpbVal(r.amount)} DPB</span>
                <span className="font-mono text-dim">{String(r.from ?? '').slice(0, 12)}…</span>
                <span className="text-dim">高度 {formatInt(r.height ?? 0)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default MerchantPage;
